<template>
  <div class="pb-page rv">
    <PageHeader title="Reports" subtitle="How many customers answer, how many testimonials go live, and how often people see them." />

    <div class="rv-filters" role="group" aria-label="Report filters">
      <div v-if="brands.length > 1" class="rv-f"><label class="pb-label" for="rv-brand">Brand</label>
        <select id="rv-brand" v-model="brandRef" class="pb-select" @change="load"><option value="all">All brands</option>
          <option v-for="item in brands" :key="item.id" :value="String(item.id)">{{ item.name }}</option></select></div>
      <div class="rv-f"><label class="pb-label" for="rv-range">Period</label>
        <select id="rv-range" v-model="preset" class="pb-select" @change="pickPreset">
          <option value="30">Last 30 days</option>
          <option value="7" :disabled="!advanced">Last 7 days{{ advanced ? '' : lockedSuffix }}</option>
          <option value="90" :disabled="!advanced">Last 90 days{{ advanced ? '' : lockedSuffix }}</option>
          <option value="custom" :disabled="!advanced">Custom range{{ advanced ? '' : lockedSuffix }}</option>
        </select></div>
      <template v-if="preset === 'custom'">
        <div class="rv-f"><label class="pb-label" for="rv-from">From</label><input id="rv-from" v-model="from" class="pb-input" type="date" :max="to || today" @change="load"></div>
        <div class="rv-f"><label class="pb-label" for="rv-to">To</label><input id="rv-to" v-model="to" class="pb-input" type="date" :min="from" :max="today" @change="load"></div>
      </template>
      <label class="pb-check rv-compare"><input type="checkbox" :checked="compare" @change="toggleCompare($event)">Compare with the previous period
        <PlanChip v-if="!advanced" :plan="reportsPlan" /></label>
    </div>

    <div v-if="!advanced" class="pb-note rv-upsell"><i class="mdi mdi-lock-outline pb-ico" aria-hidden="true"></i>
      <span>{{ reportsNote }} <router-link v-if="isOwner" :to="{ path: '/subscribe', query: { plan: reportsPlan } }">See {{ planName(reportsPlan) }}</router-link><template v-else>Ask the workspace owner.</template></span></div>

    <SkeletonList v-if="loading && !report" :rows="2" variant="tiles" label="Loading reports…" />
    <div v-else-if="error" class="pb-alert is-error" role="alert"><span>{{ error }}</span><button type="button" class="pb-button sm outline" @click="load">Retry</button></div>

    <div v-else-if="report" :class="['rv-body', { refreshing: loading }]">
      <p class="pb-muted rv-range">{{ rangeText }}</p>
      <div class="rv-tiles">
        <div v-for="tile in tiles" :key="tile.key" class="rv-tile">
          <span class="rv-t-label">{{ tile.label }}</span>
          <span class="rv-t-value">{{ number(tile.value) }}</span>
          <span v-if="tile.delta" :class="['rv-t-delta', tile.delta.tone]">
            <i :class="['mdi', tile.delta.icon]" aria-hidden="true"></i>{{ tile.delta.text }}</span>
        </div>
      </div>
      <p class="pb-muted rv-estimates"><i class="mdi mdi-information-outline" aria-hidden="true"></i> Published counts each testimonial once, the first time it goes live. Views are estimates: repeat visits count, and known bots are skipped. No cookies are used.</p>

      <EmptyState v-if="!anything" icon="mdi-chart-box-outline" title="Nothing to report yet" text="Numbers appear once your forms, widgets or wall get visits." />

      <section v-else class="pb-card rv-chart" aria-labelledby="rv-chart-title">
        <div class="pb-card-head rv-chart-head">
          <h2 id="rv-chart-title" class="pb-section-title display-font">{{ metricLabel }} per day</h2>
          <div class="pb-segmented" role="group" aria-label="Measure">
            <button v-for="item in metrics" :key="item.key" type="button" :aria-pressed="metric === item.key ? 'true' : 'false'" @click="metric = item.key">{{ item.label }}</button>
          </div>
        </div>
        <DailyChart v-if="!showTable" :series="chartSeries" :label="metricLabel" />
        <div v-else class="rv-table-wrap">
          <table class="rv-table">
            <caption class="pb-sr-only">Daily figures</caption>
            <thead><tr><th scope="col">Day</th><th v-for="item in metrics" :key="item.key" scope="col">{{ item.label }}</th></tr></thead>
            <tbody><tr v-for="row in report.series" :key="row.day"><th scope="row">{{ formatDate(row.day) }}</th>
              <td v-for="item in metrics" :key="item.key">{{ number(row[item.key]) }}</td></tr></tbody>
          </table>
        </div>
        <button type="button" class="pb-button sm ghost rv-toggle" @click="showTable = !showTable">{{ showTable ? 'Show the chart' : 'Show as a table' }}</button>
      </section>

      <section class="pb-card flat rv-alltime" aria-labelledby="rv-all">
        <h2 id="rv-all" class="pb-label">All time</h2>
        <dl class="rv-all-list">
          <div v-for="item in allTime" :key="item.key"><dt>{{ item.label }}</dt><dd>{{ number(item.value) }}</dd></div>
        </dl>
      </section>

      <section v-if="report.brands && report.brands.length" class="rv-brands" aria-labelledby="rv-brands-title">
        <h2 id="rv-brands-title" class="pb-section-title display-font rv-brands-h">By brand</h2>
        <div class="pb-card rv-table-card">
          <table class="rv-table">
            <thead><tr><th scope="col">Brand</th><th scope="col">New</th><th scope="col">In review</th><th scope="col">Awaiting client</th><th scope="col">Published</th><th scope="col">Widget views, 30 days</th></tr></thead>
            <tbody><tr v-for="row in report.brands" :key="row.id"><th scope="row"><span class="rv-swatch" :style="{ background: row.color }" aria-hidden="true"></span>{{ row.name }}</th>
              <td>{{ number(row.new) }}</td><td>{{ number(row.in_review) }}</td><td>{{ number(row.awaiting_client) }}</td><td>{{ number(row.published) }}</td><td>{{ number(row.widget_views_30d) }}</td></tr></tbody>
          </table>
        </div>
      </section>
    </div>
    <UpgradeDialog v-model="upgradeOpen" :detail="upgrade" />
  </div>
</template>

<script>
import DailyChart from '../components/DailyChart.vue';
import EmptyState from '../components/EmptyState.vue';
import PageHeader from '../components/PageHeader.vue';
import PlanChip from '../components/PlanChip.vue';
import SkeletonList from '../components/SkeletonList.vue';
import UpgradeDialog from '../components/UpgradeDialog.vue';
import workspace from '../mixins/workspace';
import { get, message, planLimit } from '../api';
import { formatDate, number } from '../lib/format';
import { allowsMore, planOrder } from '../lib/plans';

const METRICS = [
  { key: 'submissions', label: 'Responses' }, { key: 'published', label: 'Published' }, { key: 'form_visits', label: 'Form visits' },
  { key: 'widget_views', label: 'Widget views' }, { key: 'wall_views', label: 'Wall views' }
];
const TILES = [
  { key: 'submissions', label: 'Responses' }, { key: 'publishable', label: 'With permission to publish' }, { key: 'published', label: 'Published' },
  { key: 'form_visits', label: 'Form visits' }, { key: 'widget_views', label: 'Widget views' }, { key: 'wall_views', label: 'Wall views' }
];
const iso = date => date.toISOString().slice(0, 10);

export default {
  name: 'ReportsView',
  components: { DailyChart, EmptyState, PageHeader, PlanChip, SkeletonList, UpgradeDialog },
  mixins: [workspace],
  data: () => ({ report: null, loading: true, error: '', brandRef: 'all', preset: '30', from: '', to: '', compare: false, metric: 'submissions',
    showTable: false, metrics: METRICS, upgrade: null, upgradeOpen: false }),
  computed: {
    advanced() { return this.limits.reports === 'advanced'; },
    // The plans with date-range reports, from the plan matrix.
    reportsPlan() { return this.availableOn('reports') || ''; },
    lockedSuffix() { return ' (' + this.planName(this.reportsPlan) + ')'; },
    reportsNote() {
      const names = planOrder(this.planMatrix).filter(name => allowsMore(this.planMatrix[name].limits.reports, this.limits.reports))
        .map(name => this.planName(name));
      const list = names.length > 1 ? names.slice(0, -1).join(', ') + ' or ' + names[names.length - 1] : names.join('');
      return this.planLabel + ' shows the last 30 days and all-time totals. Compare any period with ' + list + '.';
    },
    today() { return iso(new Date()); },
    metricLabel() { return (METRICS.find(item => item.key === this.metric) || METRICS[0]).label; },
    chartSeries() { return (this.report ? this.report.series : []).map(row => ({ day: row.day, value: row[this.metric] || 0 })); },
    anything() {
      const sum = values => TILES.reduce((total, item) => total + (values[item.key] || 0), 0);
      return !!this.report && (sum(this.report.totals) > 0 || sum(this.report.all_time) > 0);
    },
    rangeText() {
      if (!this.report) return '';
      let text = formatDate(this.report.range.from) + ' to ' + formatDate(this.report.range.to);
      if (this.report.previous_range) text += ', compared with ' + formatDate(this.report.previous_range.from) + ' to ' + formatDate(this.report.previous_range.to);
      return text;
    },
    tiles() {
      const totals = this.report.totals;
      const previous = this.report.previous;
      return TILES.map(item => {
        const value = totals[item.key] || 0;
        let delta = null;
        if (previous) {
          const before = previous[item.key] || 0;
          const change = value - before;
          const percent = before ? Math.round((change / before) * 100) : null;
          const up = change > 0;
          delta = change === 0 ? { tone: 'flat', icon: 'mdi-minus', text: 'No change' }
            : { tone: up ? 'up' : 'down', icon: up ? 'mdi-arrow-up' : 'mdi-arrow-down',
              text: (up ? '+' : '−') + (percent === null ? Math.abs(change) : Math.abs(percent) + '%') + ' vs previous period' };
        }
        return { key: item.key, label: item.label, value, delta };
      });
    },
    allTime() { return TILES.map(item => ({ key: item.key, label: item.label, value: (this.report.all_time || {})[item.key] || 0 })); }
  },
  async mounted() {
    if (!(await this.requireWorkspace())) return;
    this.brandRef = this.brands.length > 1 ? 'all' : String(this.brandId);
    this.load();
  },
  methods: {
    number,
    formatDate,
    params() {
      const params = { brand_id: this.brandRef };
      if (!this.advanced) return params;
      if (this.preset === 'custom') { if (this.from) params.from = this.from; if (this.to) params.to = this.to; } else if (this.preset !== '30') {
        const start = new Date();
        start.setDate(start.getDate() - (Number(this.preset) - 1));
        params.from = iso(start);
        params.to = this.today;
      }
      if (this.compare) params.compare = 1;
      return params;
    },
    async load() {
      this.loading = true;
      this.error = '';
      try { this.report = await get('/reports', this.params()); } catch (error) {
        const limit = planLimit(error);
        if (limit) { this.upgrade = limit; this.upgradeOpen = true; } else this.error = message(error);
      } finally { this.loading = false; }
    },
    upsell() { this.upgrade = { feature: 'reports' }; this.upgradeOpen = true; },
    pickPreset() {
      if (!this.advanced && this.preset !== '30') { this.preset = '30'; this.upsell(); return; }
      if (this.preset === 'custom') { if (!this.to) this.to = this.today; if (!this.from) { const start = new Date(); start.setDate(start.getDate() - 29); this.from = iso(start); } }
      this.load();
    },
    toggleCompare(event) {
      if (!this.advanced) { event.target.checked = false; this.upsell(); return; }
      this.compare = event.target.checked;
      this.load();
    }
  }
};
</script>

<style scoped>
.rv-filters{display:flex;flex-wrap:wrap;align-items:flex-end;gap:12px 16px;margin-bottom:16px}
.rv-f{min-width:170px}
.rv-f .pb-label{margin-bottom:6px}
.rv-compare{min-height:44px;align-items:center!important}
.rv-upsell{margin-top:0}
.rv-body{transition:opacity .2s ease}
.rv-body.refreshing{opacity:.55}
.rv-range{margin-bottom:12px}
.rv-tiles{display:grid;grid-template-columns:repeat(6,minmax(0,1fr));gap:12px}
.rv-tile{display:flex;flex-direction:column;gap:4px;padding:16px 18px;border:1px solid #E6DED2;border-radius:14px;background:#fff}
.rv-t-label{font:600 13px/1.35 var(--font-body);color:#4F4A45}
.rv-t-value{font:600 30px/1.15 var(--font-body);color:#1F2328;letter-spacing:-.4px}
.rv-t-delta{display:inline-flex;align-items:center;gap:4px;font:600 12.5px/1.3 var(--font-body)}
.rv-t-delta.up{color:#22603C}
.rv-t-delta.down{color:#8A1C13}
.rv-t-delta.flat{color:#6B635B}
.rv-estimates{margin:10px 0 24px}
.rv-chart{margin-bottom:20px}
.rv-chart-head{flex-wrap:wrap}
.rv-toggle{margin-top:12px}
.rv-table-wrap{overflow:auto;max-height:420px;border:1px solid #EFE9E0;border-radius:10px}
.rv-table{width:100%;border-collapse:collapse;font-size:14px}
.rv-table th,.rv-table td{padding:9px 14px;text-align:left;border-bottom:1px solid #EFE9E0;white-space:nowrap}
.rv-table thead th{position:sticky;top:0;background:#FBF8F3;font:600 12.5px var(--font-body);color:#4F4A45}
.rv-table td{font-variant-numeric:tabular-nums}
.rv-alltime{margin-bottom:24px}
.rv-all-list{display:grid;grid-template-columns:repeat(6,minmax(0,1fr));gap:10px;margin:8px 0 0}
.rv-all-list dt{font-size:13px;color:#6B635B}
.rv-all-list dd{margin:2px 0 0;font:600 18px var(--font-body);color:#1F2328}
.rv-brands-h{margin-bottom:12px}
.rv-table-card{padding:0;overflow-x:auto}
.rv-swatch{display:inline-block;width:10px;height:10px;border-radius:3px;margin-right:8px}
@media(max-width:1150px){.rv-tiles,.rv-all-list{grid-template-columns:repeat(3,minmax(0,1fr))}}
@media(max-width:600px){.rv-f{flex:1 1 100%}.rv-tiles,.rv-all-list{grid-template-columns:repeat(2,minmax(0,1fr))}}
</style>
